import _forEach from "lodash/forEach";
import _get from "lodash/get";
import { AnyAction, Dispatch, Middleware, MiddlewareAPI } from "redux";
import {
    GATrackAction,
    GATrackActionType,
    GATrackAddToCartAction,
    GATrackCheckoutAndNavigateAction,
    GATrackFunnelEventAndNavigateAction,
    GATrackRemoveFromCartAction,
} from "./actionTypes";
import { State } from "./selectors";
import {
    CLIENT_EVENT_TYPES as clientEventTypes,
    ClientEventType,
    ClientEventTypeValue,
    GA_EVENTS,
} from "../../../../constants";
import { setIsGATransactionTracked } from "../../app/state/actions";
import * as APP_AT from "../../app/state/actionTypes";
import * as appSelectors from "../../app/state/selectors";
import { fulfilled } from "../../app/utils";
import { actionTypes as AUTH_AT } from "../../authentication";
import * as CHECKOUT_AT from "../../checkout/state/actionTypes";
import { startNavigating } from "../../navigation/state/actions";
import { trackFunnelEvent } from "../funnelEventHelper";
import { send } from "../GoogleAnalyticsClient";
import { trackCheckout, trackProducts, trackTransaction } from "../googleAnalyticsEventHelper";
import { trackPurchaseFromState } from "../googleAnalyticsFWAEventHelper";

// Will report all messages in the action that has type 'alert'
const ACTIONS_TO_REPORT_TO_GA = [
    fulfilled(CHECKOUT_AT.CHECKOUT_CHECK_EXISTING_PHONE_NUMBER),
    fulfilled(CHECKOUT_AT.CHECKOUT_CHECK_NEW_MEMBER_PHONE_NUMBER),
];

export const reportToGoogleAnalytics = async (triggeringAction: ClientEventType, message: string): Promise<unknown> => {
    const eventAction: ClientEventTypeValue = clientEventTypes[triggeringAction] || triggeringAction;
    const gaData = {
        event: GA_EVENTS.SHOP_ERROR,
        eventCategory: "WebShop",
        eventAction,
        eventLabel: message,
    };
    return send(gaData);
};

const handleGATrackingActions = (
    type: GATrackActionType,
    action: GATrackAction,
    _store: MiddlewareAPI<Dispatch, State>,
): Promise<unknown> => {
    if (type === "TRACKING_ADD_ITEM_TO_CART") {
        const { payload } = action as GATrackAddToCartAction;
        return trackProducts(payload.state, "add");
    } else if (type === "TRACKING_REMOVE_ITEM_FROM_CART") {
        const { payload } = action as GATrackRemoveFromCartAction;
        return trackProducts(payload.state, "remove");
    } else if (type === "TRACKING_CHECKOUT") {
        const { payload } = action as GATrackCheckoutAndNavigateAction;
        const { step, url } = payload;
        const navigate = (): void => {
            if (url) {
                _store.dispatch(startNavigating(url));
                window.location.assign(url);
            }
        };
        // Do navigation regardless of success or fail
        return trackCheckout(step, _store.getState()).then(navigate).catch(navigate);
    } else if (type === "TRACKING_REFILL_CHECKOUT") {
        const { payload } = action as GATrackCheckoutAndNavigateAction;
        const { url } = payload;
        const navigate = (): void => {
            if (url) {
                _store.dispatch(startNavigating(url));
                window.location.assign(url);
            }
        };
        // TODO: Track refill checkout in custom events or reuse todays checkout event?
        // return trackCheckout(step, _store.getState()).then(navigate).catch(navigate);
        return Promise.resolve(navigate());
    } else if (type === "TRACKING_FUNNEL") {
        const { payload } = action as GATrackFunnelEventAndNavigateAction;
        const { trackingObj, url } = payload;
        const navigate = (): void => {
            if (url) {
                _store.dispatch(startNavigating(url));
                window.location.assign(url);
            }
        };
        // Do navigation regardless of success or fail
        return trackFunnelEvent(trackingObj).then(navigate).catch(navigate);
    }
    return Promise.resolve();
};

const shouldTrackTransactionFwa = (path: string, store: State): boolean => {
    return (
        !!path &&
        !!path.match(/^\/internett\/adressesok\/kvittering\/\w*\/$/) &&
        appSelectors.isFwaCompleted(store) &&
        !appSelectors.isGATransactionTracked(store)
    );
};

/**
 * 1. Report UI errors to Google Analytics so the GA people can track them
 * 2. TODO: Report relevant user actions (selecting a particular product, ...) to GA
 */
export default (): Middleware<unknown, State> => (_store) => (next) => (action: AnyAction) => {
    const { type, meta = {}, payload, data = {} } = action;
    const { isAuthenticated = false } = data;
    // NOTE: See asyncErrorHandlingMiddleware.js that produces the SET_FLASH event
    if (type === APP_AT.GENERAL_SET_FLASH && payload.type === "negative") {
        reportToGoogleAnalytics(meta.gaEvent, payload.message);
    }

    if (ACTIONS_TO_REPORT_TO_GA.includes(type)) {
        const messages = _get(payload, "messages", []);
        _forEach(messages, (message) => {
            if (message.type === "alert" && message.text) {
                reportToGoogleAnalytics(type, message.text);
            }
        });
    }

    // AUTHENTICATION TRACKING
    if (type === AUTH_AT.AUTH_LOGOUT_SUCCEEDED || type === AUTH_AT.AUTH_IS_AUTHENTICATED_SUCCEEDED) {
        send({
            userAuthenticated: isAuthenticated.toString(),
            event: "authentication",
        });
    }

    // GENERAL TRACKING
    if (type.indexOf("TRACKING_") === 0) {
        handleGATrackingActions(type, action as GATrackAction, _store);
    }

    // FWA
    if (type === APP_AT.APP_PAGE_LOADED) {
        const store = _store.getState();
        if (shouldTrackTransactionFwa(payload.path, store)) {
            trackPurchaseFromState(_store.getState());
            _store.dispatch(setIsGATransactionTracked(true));
        }
    }

    // B2C
    if (type === APP_AT.APP_PAGE_LOADED) {
        const store = _store.getState();
        if (
            payload.path &&
            payload.path.match(/^\/kasse\/kvittering\/\w*\/$/) &&
            appSelectors.isCheckoutCompleted(store) &&
            !appSelectors.isGATransactionTracked(store)
        ) {
            trackTransaction(store);
            _store.dispatch(setIsGATransactionTracked(true));
        }
    }

    // B2B
    if (type === APP_AT.APP_PAGE_LOADED) {
        const store = _store.getState();
        if (
            payload.path &&
            payload.path.match(/^\/bedrift\/kasse\/kvittering\/\w*\/$/) &&
            appSelectors.isCheckoutCompleted(store) &&
            !appSelectors.isGATransactionTracked(store)
        ) {
            trackTransaction(store);
            _store.dispatch(setIsGATransactionTracked(true));
        }
    }
    return next(action);
};
